<script setup>
import BackButton from "@/components/utils/BackButton.vue";
import LabelledField from "@/components/utils/LabelledField.vue";
import apiCall from "@/utils/api";
import { getFileDownloadUrl } from "@/utils/functions";
import { ref, computed, onMounted, watch } from "vue";
import { hasPermission, EPORTAL_SCHEME_PLAN_APPLICATION_CREATE } from "@/utils/permissions";
import { formatSchemePlanApplicationStage } from "./scheme_plan_application_business_logic";
import pdf from 'vue-pdf';

const props = defineProps({
  application: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["goBackClicked"]);

// Amendment form state
const isDeferred = computed(() => (props.application.status || "").toLowerCase() === "deferred");
const canCreateApplication = computed(() => hasPermission(EPORTAL_SCHEME_PLAN_APPLICATION_CREATE));
const categories = ref([]);
const types = ref([]);
const supportingDocuments = ref([]);
const amendCategory = ref(props.application.scheme_plan_category_id);
const amendType = ref(props.application.scheme_plan_type_id);
const amendRemarks = ref(props.application.remarks || "");
const amendFiles = ref({});
const submittingAmendment = ref(false);
const amendmentMessage = ref("");
const amendmentColor = ref("");
const showAmendmentSnackbar = ref(false);

onMounted(async () => {
  if (isDeferred.value) {
    try {
      const resp = await apiCall({
        url: "/api/client/scheme-plan-application/form-data",
        method: "GET",
      });
      categories.value = resp.categories || [];
      types.value = resp.types || [];
      supportingDocuments.value = resp.supporting_documents || [];
    } catch (e) {
      console.error("Failed to load form data", e);
    }
  }
});

// Get feedback for a specific document
function getDocumentFeedback(doc) {
  if (!props.application.document_feedbacks) return null;
  const docName = doc.supporting_document ? doc.supporting_document.name : doc.original_name;
  return props.application.document_feedbacks.find(
    (fb) => fb.document_name === docName
  );
}

function getLatestComment(feedback) {
  if (!feedback || !feedback.feedback || feedback.feedback.length === 0) return "";
  const latest = feedback.feedback[feedback.feedback.length - 1];
  return latest.comment || "";
}

function onFileSelected(docId, event) {
  const file = event?.target?.files?.[0] || event;
  if (file) {
    amendFiles.value[docId] = file;
  }
}

async function submitAmendment() {
  submittingAmendment.value = true;
  try {
    const formData = new FormData();
    formData.append("scheme_plan_category_id", amendCategory.value);
    formData.append("scheme_plan_type_id", amendType.value);
    if (amendRemarks.value) {
      formData.append("remarks", amendRemarks.value);
    }

    // Append re-uploaded files
    for (const [docId, file] of Object.entries(amendFiles.value)) {
      formData.append(`documents[${docId}]`, file);
    }

    await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/amend`,
      data: formData,
      method: "POST",
    });

    amendmentMessage.value = "Amendment submitted successfully";
    amendmentColor.value = "success";
    showAmendmentSnackbar.value = true;

    setTimeout(() => {
      emit("goBackClicked");
    }, 1500);
  } catch (error) {
    amendmentMessage.value =
      error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat()[0]
        : 'An error occurred. Please try again.';
    amendmentColor.value = "error";
    showAmendmentSnackbar.value = true;
  } finally {
    submittingAmendment.value = false;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStatusColor(status) {
  const statusLower = (status || "").toLowerCase();
  if (statusLower === "approved") return "success";
  if (statusLower === "paid") return "success";
  if (statusLower === "rejected") return "error";
  if (statusLower === "pending") return "warning";
  if (statusLower === "deferred") return "deep-orange";
  if (statusLower === "in_progress") return "info";
  return "grey";
}

const viewingDocId = ref(null);
const downloadingDocId = ref(null);

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ----- Document version grouping -----------------------------------------
// Groups all documents by supporting_document type, newest version first.
const documentGroups = computed(() => {
  const docs = props.application.documents || [];
  const groups = {};
  docs.forEach((doc) => {
    const key = doc.scheme_plan_supporting_document_id;
    if (!groups[key]) groups[key] = [];
    groups[key].push(doc);
  });
  // Sort each group descending by version so index [0] is the current version
  Object.values(groups).forEach((g) => g.sort((a, b) => (b.version || 1) - (a.version || 1)));
  return Object.values(groups);
});

// Track which document type groups have their history expanded
const expandedDocGroups = ref({});
function toggleDocHistory(key) {
  expandedDocGroups.value = {
    ...expandedDocGroups.value,
    [key]: !expandedDocGroups.value[key],
  };
}

// Approved documents: current-version docs that qualify (Scheme plan duly signed)
const isApproved = computed(() => (props.application.status || '').toLowerCase() === 'approved');
const APPROVED_DOC_KEYWORDS = ['scheme'];
const approvedDocuments = computed(() => {
  if (!isApproved.value) return [];
  return documentGroups.value
    .map((group) => group[0]) // current version per type
    .filter((doc) => {
      const name = (doc.supporting_document?.name || '').toLowerCase();
      return APPROVED_DOC_KEYWORDS.some((kw) => name.includes(kw));
    });
});
// -------------------------------------------------------------------------

async function viewDocument(doc) {
  viewingDocId.value = doc.id;
  const name = doc.supporting_document?.name || doc.original_name || 'Document';
  const ext  = (doc.original_name || '').split('.').pop().toLowerCase();
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { name, url: '', filename: doc.original_name || 'document', isPdf: ext === 'pdf' || ext === '' };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/document/${doc.id}`,
      method: "GET",
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(blob);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error("Failed to load document", e);
    docViewerDialog.value = false;
  } finally {
    docLoading.value = false;
    viewingDocId.value = null;
  }
}

async function downloadDocument(doc) {
  downloadingDocId.value = doc.id;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/document/${doc.id}`,
      method: "GET",
      responseType: "blob",
    });
    triggerBlobDownload(blob, doc.original_name || 'document.pdf');
  } catch (e) {
    console.error("Failed to download document", e);
  } finally {
    downloadingDocId.value = null;
  }
}

const downloadingPlupaDc8 = ref(false);
async function downloadPlupaDc8() {
  downloadingPlupaDc8.value = true;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/plupa-dc8`,
      method: "GET",
      responseType: "blob",
    });
    triggerBlobDownload(blob, 'PLUPA_DC8_' + props.application.application_number + '.pdf');
  } catch (e) {
    console.error("Failed to download PLUPA/DC/8", e);
  } finally {
    downloadingPlupaDc8.value = false;
  }
}

const viewingApprovedDocId = ref(null);

// ── Document viewer ──────────────────────────────────────────────────────────
const docViewerDialog = ref(false);
const docLoading      = ref(false);
const viewerKey       = ref(0);
const viewingDoc      = ref({ name: '', url: '', filename: '', isPdf: true });
let _currentBlobUrl   = null;

function _revokeBlobUrl() {
  if (_currentBlobUrl) {
    URL.revokeObjectURL(_currentBlobUrl);
    _currentBlobUrl = null;
  }
}

watch(docViewerDialog, (isOpen) => {
  if (!isOpen) {
    _revokeBlobUrl();
    viewingDoc.value = { name: '', url: '', filename: '', isPdf: true };
  }
});

function downloadViewedDoc() {
  if (!_currentBlobUrl) return;
  const a = document.createElement('a');
  a.href = _currentBlobUrl;
  a.download = viewingDoc.value.filename || 'document.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
// ────────────────────────────────────────────────────────────────────────────

async function viewApprovedDocument(doc) {
  viewingApprovedDocId.value = doc.id;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/approved-document/${doc.id}`,
      method: "GET",
      responseType: "blob",
    });
    triggerBlobDownload(blob, 'Approved_' + (doc.original_name || 'document.pdf'));
  } catch (e) {
    console.error("Failed to download approved document", e);
  } finally {
    viewingApprovedDocId.value = null;
  }
}

const viewingPlupaDc8 = ref(false);
async function viewPlupaDc8() {
  viewingPlupaDc8.value = true;
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { name: 'PLUPA/DC/8', url: '', filename: `PLUPA_DC8_${props.application.application_number}.pdf`, isPdf: true };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/plupa-dc8`,
      method: "GET",
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(blob);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error("Failed to load PLUPA/DC/8", e);
    docViewerDialog.value = false;
  } finally {
    docLoading.value = false;
    viewingPlupaDc8.value = false;
  }
}

const viewingApprovedDocInViewerId = ref(null);
async function viewApprovedDocInViewer(doc) {
  viewingApprovedDocInViewerId.value = doc.id;
  const name = doc.supporting_document?.name || doc.original_name || 'Approved Document';
  const ext  = (doc.original_name || '').split('.').pop().toLowerCase();
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { name, url: '', filename: doc.original_name || 'document', isPdf: ext === 'pdf' || ext === '' };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/approved-document/${doc.id}`,
      method: "GET",
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(blob);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error("Failed to load approved document", e);
    docViewerDialog.value = false;
  } finally {
    docLoading.value = false;
    viewingApprovedDocInViewerId.value = null;
  }
}

const receiptNumber = computed(() => {
  const invoices = props.application.bill?.bills_invoices;
  if (!invoices || invoices.length === 0) return null;
  return invoices[0]?.invoice_receipt?.receipt?.receipt_number || null;
});

const viewingBill = ref(false);
const viewingReceipt = ref(false);

// ── LR Formalisation ─────────────────────────────────────────────────────────
const isSubdivision = computed(() =>
  (props.application.scheme_plan_type?.name || '').toLowerCase().includes('subdivision')
);
const isAmalgamation = computed(() =>
  (props.application.scheme_plan_type?.name || '').toLowerCase().includes('amalgamation')
);
const showLrFormalisationSection = computed(() =>
  isApproved.value && (isSubdivision.value || isAmalgamation.value)
);

const lrFetchLoading = ref(false);
const lrFetched = ref(false);
const lrFormalisationData = ref(null);
const lrApplicationType = ref('');
const lrChildren = ref([]);

const lrApprovedSchemeFile = ref(null);
const lrMutationFormsFile = ref(null);
const lrSubdivisionAssignments = ref({});
const lrAmalgamationAdjudication = ref(null);
const lrAmalgamationParcel = ref('');
const lrAdjudications = ref([]);
const lrLoadingAdjudications = ref(false);
const lrSubmitting = ref(false);
const lrMessage = ref('');
const lrMessageColor = ref('');
const showLrSnackbar = ref(false);

const viewingLrDocType = ref(null);
const downloadingLrDocType = ref(null);
const downloadingLrDc10 = ref(false);
const viewingLrDc10 = ref(false);

const lrHasDuplicateLrNumbers = computed(() => {
  if (lrApplicationType.value !== 'subdivision') return false;
  const vals = Object.values(lrSubdivisionAssignments.value)
    .map((v) => ((v.adjudication?.name || v.adjudication || '').trim() + ' ' + (v.parcel || '').trim()).toLowerCase())
    .filter(Boolean);
  return vals.length !== new Set(vals).size;
});

const lrCanSubmit = computed(() => {
  if (!lrApprovedSchemeFile.value || !lrMutationFormsFile.value) return false;
  if (lrApplicationType.value === 'subdivision') {
    const allFilled = Object.values(lrSubdivisionAssignments.value).every(
      (v) => v && v.adjudication && v.parcel && v.parcel.trim() !== ''
    );
    return allFilled && !lrHasDuplicateLrNumbers.value;
  }
  if (lrApplicationType.value === 'amalgamation') {
    return !!lrAmalgamationAdjudication.value && lrAmalgamationParcel.value.trim() !== '';
  }
  return false;
});

async function fetchLrFormalisation() {
  lrFetchLoading.value = true;
  try {
    const resp = await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/lr-formalisation`,
      method: 'GET',
    });
    lrFormalisationData.value = resp.formalisation;
    lrApplicationType.value = resp.application_type || '';
    lrChildren.value = resp.children || [];
    if (!lrFormalisationData.value && lrChildren.value.length > 0) {
      const map = {};
      lrChildren.value.forEach((c) => { map[c.id] = { adjudication: '', parcel: '' }; });
      lrSubdivisionAssignments.value = map;
    }
    lrFetched.value = true;
  } catch (e) {
    console.error('Failed to fetch LR formalisation', e);
  } finally {
    lrFetchLoading.value = false;
  }
}

async function fetchLrAdjudications() {
  if (lrAdjudications.value.length > 0) return;
  lrLoadingAdjudications.value = true;
  try {
    const data = await apiCall({ url: '/api/client/scheme-plan-property/form-data', method: 'GET' });
    lrAdjudications.value = data.adjudications || [];
  } catch (e) {
    console.error('Failed to load adjudications', e);
  } finally {
    lrLoadingAdjudications.value = false;
  }
}

watch(showLrFormalisationSection, (val) => {
  if (val) {
    if (!lrFetched.value) fetchLrFormalisation();
    fetchLrAdjudications();
  }
}, { immediate: true });

watch(() => props.application.id, () => {
  // Reset all LR state when the viewed application changes
  lrFetched.value = false;
  lrFormalisationData.value = null;
  lrApplicationType.value = '';
  lrChildren.value = [];
  lrSubdivisionAssignments.value = {};
  lrAmalgamationAdjudication.value = null;
  lrAmalgamationParcel.value = '';
  lrApprovedSchemeFile.value = null;
  lrMutationFormsFile.value = null;
  if (showLrFormalisationSection.value) {
    fetchLrFormalisation();
    fetchLrAdjudications();
  }
});

async function viewLrDocument(type) {
  viewingLrDocType.value = type;
  const label = type === 'approved_scheme_plan' ? 'Approved Scheme Plan' : 'Mutation Forms';
  const filename = (type === 'approved_scheme_plan' ? 'Approved_Scheme_Plan' : 'Mutation_Forms') + '_' + lrFormalisationData.value.id + '.pdf';
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { name: label, url: '', filename, isPdf: true };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-lr-formalisation/${lrFormalisationData.value.id}/document/${type}`,
      method: 'GET',
      responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(blob);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error('Failed to load LR formalisation document', e);
    docViewerDialog.value = false;
  } finally {
    docLoading.value = false;
    viewingLrDocType.value = null;
  }
}

async function downloadLrDocument(type) {
  downloadingLrDocType.value = type;
  const label = type === 'approved_scheme_plan' ? 'Approved_Scheme_Plan' : 'Mutation_Forms';
  const filename = label + '_' + lrFormalisationData.value.id + '.pdf';
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-lr-formalisation/${lrFormalisationData.value.id}/document/${type}?download=1`,
      method: 'GET',
      responseType: 'blob',
    });
    triggerBlobDownload(blob, filename);
  } catch (e) {
    console.error('Failed to download LR formalisation document', e);
  } finally {
    downloadingLrDocType.value = null;
  }
}

async function viewLrDc10() {
  viewingLrDc10.value = true;
  const pn = lrFormalisationData.value.scheme_plan_property?.pn || lrFormalisationData.value.id;
  const filename = `PLUPA_DC10_${pn}.pdf`;
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { name: 'PLUPA/DC/10', url: '', filename, isPdf: true };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-lr-formalisation/${lrFormalisationData.value.id}/plupa-dc10`,
      method: 'GET',
      responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(blob);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error('Failed to load PLUPA/DC/10', e);
    docViewerDialog.value = false;
  } finally {
    docLoading.value = false;
    viewingLrDc10.value = false;
  }
}

async function downloadLrDc10() {
  downloadingLrDc10.value = true;
  const pn = lrFormalisationData.value.scheme_plan_property?.pn || lrFormalisationData.value.id;
  const filename = `PLUPA_DC10_${pn}.pdf`;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-lr-formalisation/${lrFormalisationData.value.id}/plupa-dc10`,
      method: 'GET',
      responseType: 'blob',
    });
    triggerBlobDownload(blob, filename);
  } catch (e) {
    console.error('Failed to download PLUPA/DC/10', e);
  } finally {
    downloadingLrDc10.value = false;
  }
}

async function submitLrFormalisation() {
  lrSubmitting.value = true;
  try {
    const formData = new FormData();
    formData.append('approved_scheme_plan', lrApprovedSchemeFile.value);
    formData.append('mutation_forms', lrMutationFormsFile.value);

    if (lrApplicationType.value === 'subdivision') {
      Object.entries(lrSubdivisionAssignments.value).forEach(([propId, parts], idx) => {
        const adjName = parts.adjudication?.name || parts.adjudication || '';
        const combined = adjName.trim() + ' ' + (parts.parcel || '').trim();
        formData.append(`assignment_data[${idx}][property_id]`, propId);
        formData.append(`assignment_data[${idx}][new_lr_number]`, combined.trim());
      });
    } else {
      const adjName = lrAmalgamationAdjudication.value?.name || lrAmalgamationAdjudication.value || '';
      const combined = adjName.trim() + ' ' + lrAmalgamationParcel.value.trim();
      formData.append('assignment_data[new_lr_number]', combined.trim());
    }

    await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/lr-formalisation`,
      data: formData,
      method: 'POST',
    });

    lrMessage.value = 'LR Formalisation submitted successfully and sent for approval.';
    lrMessageColor.value = 'success';
    showLrSnackbar.value = true;
    await fetchLrFormalisation();
  } catch (error) {
    lrMessage.value = error?.response?.data?.errors
      ? Object.values(error.response.data.errors).flat()[0]
      : (error?.response?.data?.message || 'An error occurred. Please try again.');
    lrMessageColor.value = 'error';
    showLrSnackbar.value = true;
  } finally {
    lrSubmitting.value = false;
  }
}
// ────────────────────────────────────────────────────────────────────────────

async function viewBill() {
  if (!props.application.bill?.id) return;
  viewingBill.value = true;
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { name: 'Bill - ' + props.application.bill.bill_number, url: '', filename: 'Bill_' + props.application.bill.bill_number + '.pdf', isPdf: true };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const url = getFileDownloadUrl({ billId: props.application.bill.id });
    const blob = await apiCall({ url, method: "GET", responseType: "blob" });
    const blobUrl = URL.createObjectURL(blob);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error("Failed to load bill", e);
    docViewerDialog.value = false;
  } finally {
    docLoading.value = false;
    viewingBill.value = false;
  }
}

async function viewReceipt() {
  if (!receiptNumber.value) return;
  viewingReceipt.value = true;
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { name: 'Receipt - ' + receiptNumber.value, url: '', filename: 'Receipt_' + receiptNumber.value + '.pdf', isPdf: true };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const url = getFileDownloadUrl({ receiptNumber: receiptNumber.value });
    const blob = await apiCall({ url, method: "GET", responseType: "blob" });
    const blobUrl = URL.createObjectURL(blob);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error("Failed to load receipt", e);
    docViewerDialog.value = false;
  } finally {
    docLoading.value = false;
    viewingReceipt.value = false;
  }
}
</script>

<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="12" md="1">
        <BackButton @click="emit('goBackClicked')">Back</BackButton>
      </v-col>
      <v-col>
        <div class="text-h6 text-center font-weight-bold primary--text">
          Application Details
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="12">
        <v-card outlined class="pa-6">
          <v-row>
            <v-col cols="12" md="4">
              <LabelledField label="Application Number">
                <div class="font-weight-bold grey--text text--darken-2">
                  {{ application.application_number }}
                </div>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField label="Status">
                <v-chip small :color="getStatusColor(application.status)" dark>
                  {{ application.status }}
                </v-chip>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField label="Stage">
                <div class="grey--text text--darken-2">
                  {{ formatSchemePlanApplicationStage(application.stage) }}
                </div>
              </LabelledField>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <v-row>
            <v-col cols="12" md="6">
              <LabelledField label="Property Number (PN)">
                <div class="grey--text text--darken-2">
                  {{ application.scheme_plan_property?.pn || "-" }}
                </div>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="6">
              <LabelledField label="LR Number">
                <div class="grey--text text--darken-2">
                  {{ application.scheme_plan_property?.lr_number || "-" }}
                </div>
              </LabelledField>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <LabelledField label="SubCounty">
                <div class="grey--text text--darken-2">
                  {{
                    application.scheme_plan_property?.sub_county
                      ?.sub_county_name || "-"
                  }}
                </div>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="6">
              <LabelledField label="Ward">
                <div class="grey--text text--darken-2">
                  {{
                    application.scheme_plan_property?.ward?.ward_name || "-"
                  }}
                </div>
              </LabelledField>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <LabelledField label="Town">
                <div class="grey--text text--darken-2">
                  {{
                    application.scheme_plan_property?.town?.town_name || "-"
                  }}
                </div>
              </LabelledField>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <div class="text-subtitle-2 font-weight-bold mb-2">Property Owner(s)</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Name</th>
                <th>ID Number</th>
                <th>Phone</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="application.client">
                <td>{{ application.client.customer_name || "-" }}</td>
                <td>{{ application.client.id_number || "-" }}</td>
                <td>{{ application.client.phone_number1 || "-" }}</td>
                <td>{{ application.client.email_address || "-" }}</td>
              </tr>
              <tr v-else>
                <td colspan="4" class="text-center grey--text">No owner information available</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-divider class="my-4" />

          <v-row>
            <v-col cols="12" md="6">
              <LabelledField label="Category">
                <div class="grey--text text--darken-2">
                  {{ application.scheme_plan_category?.name || "-" }}
                </div>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="6">
              <LabelledField label="Type">
                <div class="grey--text text--darken-2">
                  {{ application.scheme_plan_type?.name || "-" }}
                </div>
              </LabelledField>
            </v-col>
          </v-row>

          <v-row v-if="application.remarks">
            <v-col cols="12">
              <LabelledField label="Remarks">
                <div class="grey--text text--darken-2">{{ application.remarks }}</div>
              </LabelledField>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <LabelledField label="Date Submitted">
                <div class="grey--text text--darken-2">{{ formatDate(application.created_at) }}</div>
              </LabelledField>
            </v-col>
          </v-row>

          <!-- Uploaded Documents (version-aware, grouped by type) -->
          <template v-if="application.documents && application.documents.length > 0">
            <v-divider class="my-4" />
            <div class="text-subtitle-2 font-weight-bold mb-2">
              Uploaded Documents
            </div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Document Type</th>
                  <th>File Name</th>
                  <th>Version</th>
                  <th></th>
                  <th v-if="isDeferred">Feedback</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in documentGroups">
                  <!-- Current (latest) version row -->
                  <tr :key="group[0].scheme_plan_supporting_document_id + '_cur'">
                    <td>{{ group[0].supporting_document?.name || "-" }}</td>
                    <td>{{ group[0].original_name }}</td>
                    <td>
                      <v-chip small color="primary" dark>v{{ group[0].version || 1 }} (Current)</v-chip>
                    </td>
                    <td>
                      <div class="d-flex flex-nowrap align-center">
                        <v-btn x-small outlined color="primary" :loading="viewingDocId === group[0].id"
                          @click="viewDocument(group[0])">
                          <v-icon x-small left>mdi-eye</v-icon> View
                        </v-btn>
                        <v-btn x-small outlined color="success" :loading="downloadingDocId === group[0].id"
                          @click="downloadDocument(group[0])" class="ml-1">
                          <v-icon x-small left>mdi-download</v-icon> Download
                        </v-btn>
                        <!-- Toggle history button when older versions exist -->
                        <v-btn v-if="group.length > 1" x-small text color="grey darken-1" class="ml-1"
                          @click="toggleDocHistory(group[0].scheme_plan_supporting_document_id)">
                          <v-icon x-small>{{ expandedDocGroups[group[0].scheme_plan_supporting_document_id] ?
                            'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          History ({{ group.length - 1 }})
                        </v-btn>
                      </div>
                    </td>
                    <td v-if="isDeferred">
                      <template v-if="getDocumentFeedback(group[0])">
                        <v-chip small :color="getDocumentFeedback(group[0]).reattach_document ? 'error' : 'success'"
                          dark class="mr-1">
                          {{ getDocumentFeedback(group[0]).reattach_document ? 'Re-upload Required' : 'OK' }}
                        </v-chip>
                        <span class="text-caption">{{ getLatestComment(getDocumentFeedback(group[0])) }}</span>
                      </template>
                      <span v-else class="text-caption grey--text">No feedback</span>
                    </td>
                  </tr>
                  <!-- Older version rows (collapsed by default) -->
                  <template v-if="expandedDocGroups[group[0].scheme_plan_supporting_document_id]">
                    <tr v-for="oldDoc in group.slice(1)" :key="oldDoc.id" style="background: #fafafa;">
                      <td class="grey--text pl-8">↳ Previous version</td>
                      <td class="grey--text">{{ oldDoc.original_name }}</td>
                      <td>
                        <v-chip small color="grey" outlined>v{{ oldDoc.version || '-' }}</v-chip>
                      </td>
                      <td>
                        <div class="d-flex flex-nowrap align-center">
                          <v-btn x-small outlined color="grey darken-1" :loading="viewingDocId === oldDoc.id"
                            @click="viewDocument(oldDoc)">
                            <v-icon x-small left>mdi-eye</v-icon> View
                          </v-btn>
                          <v-btn x-small outlined color="secondary" :loading="downloadingDocId === oldDoc.id"
                            @click="downloadDocument(oldDoc)" class="ml-1">
                            <v-icon x-small left>mdi-download</v-icon> Download
                          </v-btn>
                        </div>
                      </td>
                      <td v-if="isDeferred"></td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </v-simple-table>
          </template>

          <!-- Bill & Payment -->
          <template v-if="application.bill">
            <v-divider class="my-4" />
            <div class="text-subtitle-2 font-weight-bold mb-2">Bill &amp; Payment</div>
            <v-row no-gutters>
              <!-- Left: Bill -->
              <v-col cols="12" :md="receiptNumber ? 6 : 12" :class="receiptNumber ? 'pr-md-3' : ''">
                <div class="text-caption grey--text text--darken-1 font-weight-medium mb-1">Bill</div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Bill Number</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ application.bill.bill_number }}</td>
                      <td>
                        <v-chip small :color="getStatusColor(application.bill.work_flow_status?.status_name)" dark>
                          {{ application.bill.work_flow_status?.status_name || "-" }}
                        </v-chip>
                      </td>
                      <td>
                        <v-btn x-small outlined color="primary" :loading="viewingBill" @click="viewBill">
                          <v-icon x-small left>mdi-eye</v-icon>
                          View Bill
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>

              <!-- Right: Receipt (only when paid) -->
              <v-col v-if="receiptNumber" cols="12" md="6" class="pl-md-3 mt-4 mt-md-0">
                <div class="text-caption grey--text text--darken-1 font-weight-medium mb-1">Receipt</div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Receipt Number</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ receiptNumber }}</td>
                      <td>
                        <v-btn x-small outlined color="success" :loading="viewingReceipt" @click="viewReceipt">
                          <v-icon x-small left>mdi-eye</v-icon>
                          View Receipt
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </template>

          <!-- Approved Documents -->
          <template v-if="isApproved">
            <v-divider class="my-4" />
            <div class="text-subtitle-2 font-weight-bold mb-2">Approved Documents</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Document Type</th>
                  <th>File Name</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <!-- Generated PLUPA/DC/8 -->
                <tr>
                  <td>PLUPA/DC/8</td>
                  <td>&mdash;</td>
                  <td>
                    <v-btn x-small outlined color="primary" :loading="viewingPlupaDc8" class="mr-1"
                      @click="viewPlupaDc8">
                      <v-icon x-small left>mdi-eye</v-icon>
                      View
                    </v-btn>
                    <v-btn x-small outlined color="success" :loading="downloadingPlupaDc8"
                      @click="downloadPlupaDc8">
                      <v-icon x-small left>mdi-download</v-icon>
                      Download
                    </v-btn>
                  </td>
                </tr>
                <!-- Scheme plan duly signed (uploaded, stamp-overlaid) -->
                <tr v-for="doc in approvedDocuments" :key="'approved-' + doc.id">
                  <td>{{ doc.supporting_document?.name || "-" }}</td>
                  <td>{{ doc.original_name }}</td>
                  <td>
                    <v-btn x-small outlined color="primary" :loading="viewingApprovedDocInViewerId === doc.id" class="mr-1"
                      @click="viewApprovedDocInViewer(doc)">
                      <v-icon x-small left>mdi-eye</v-icon>
                      View
                    </v-btn>
                    <v-btn x-small outlined color="success" :loading="viewingApprovedDocId === doc.id"
                      @click="viewApprovedDocument(doc)">
                      <v-icon x-small left>mdi-download</v-icon>
                      Download
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <!-- LR Formalisation -->
          <template v-if="showLrFormalisationSection">
            <v-divider class="my-4" />
            <div class="text-subtitle-2 font-weight-bold mb-2">LR Formalisation</div>

            <div v-if="lrFetchLoading" class="d-flex justify-center py-4">
              <v-progress-circular indeterminate color="primary" size="32" />
            </div>

            <template v-else-if="lrFetched">
              <!-- Existing formalisation record -->
              <template v-if="lrFormalisationData">
                <v-row class="mb-2 ml-3" no-gutters>
                  <v-col cols="6">
                    <LabelledField label="Status">
                      <v-chip small :color="getStatusColor(lrFormalisationData.status)" dark>
                        {{ lrFormalisationData.status }}
                      </v-chip>
                    </LabelledField>
                  </v-col>
                  <v-col cols="6">
                    <LabelledField label="Type">
                      <div class="grey--text text--darken-2 text-capitalize">{{ lrFormalisationData.type }}</div>
                    </LabelledField>
                  </v-col>
                </v-row>

                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lrFormalisationData.approved_scheme_plan_path">
                      <td>Approved Scheme Plan</td>
                      <td>
                        <v-btn x-small outlined color="primary" :loading="viewingLrDocType === 'approved_scheme_plan'" class="mr-1"
                          @click="viewLrDocument('approved_scheme_plan')">
                          <v-icon x-small left>mdi-eye</v-icon> View
                        </v-btn>
                        <v-btn x-small outlined color="success" :loading="downloadingLrDocType === 'approved_scheme_plan'"
                          @click="downloadLrDocument('approved_scheme_plan')">
                          <v-icon x-small left>mdi-download</v-icon> Download
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="lrFormalisationData.mutation_forms_path">
                      <td>Mutation Forms</td>
                      <td>
                        <v-btn x-small outlined color="primary" :loading="viewingLrDocType === 'mutation_forms'" class="mr-1"
                          @click="viewLrDocument('mutation_forms')">
                          <v-icon x-small left>mdi-eye</v-icon> View
                        </v-btn>
                        <v-btn x-small outlined color="success" :loading="downloadingLrDocType === 'mutation_forms'"
                          @click="downloadLrDocument('mutation_forms')">
                          <v-icon x-small left>mdi-download</v-icon> Download
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="lrFormalisationData.status === 'Approved'">
                      <td>PLUPA/DC/10</td>
                      <td>
                        <v-btn x-small outlined color="primary" :loading="viewingLrDc10" class="mr-1"
                          @click="viewLrDc10()">
                          <v-icon x-small left>mdi-eye</v-icon> View
                        </v-btn>
                        <v-btn x-small outlined color="success" :loading="downloadingLrDc10"
                          @click="downloadLrDc10()">
                          <v-icon x-small left>mdi-download</v-icon> Download
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </template>

              <!-- LR Formalisation submission form (not yet submitted) -->
              <template v-else>
                <v-alert type="info" dense outlined class="mb-4">
                  Please upload the Approved Scheme Plan and Mutation Forms, then assign LR numbers to initiate the LR Formalisation process.
                </v-alert>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-file-input v-model="lrApprovedSchemeFile" label="Approved Scheme Plan (PDF)" outlined dense
                      accept="application/pdf" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-file-input v-model="lrMutationFormsFile" label="Mutation Forms (PDF)" outlined dense
                      accept="application/pdf" />
                  </v-col>
                </v-row>

                <!-- Subdivision: table of child properties with LR number inputs -->
                <template v-if="lrApplicationType === 'subdivision' && lrChildren.length > 0">
                  <div class="text-subtitle-2 font-weight-bold mb-2 mt-2">Assign LR Numbers to Sub-plots</div>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>PN</th>
                        <th>Area (m²)</th>
                        <th>Adjudication</th>
                        <th>Parcel Number</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="child in lrChildren" :key="child.id">
                        <td>{{ child.pn }}</td>
                        <td>{{ child.size_sqm || '-' }}</td>
                        <td>
                          <v-autocomplete v-model="lrSubdivisionAssignments[child.id].adjudication"
                            :items="lrAdjudications" :loading="lrLoadingAdjudications"
                            item-text="name" item-value="id" return-object
                            label="Adjudication" outlined dense hide-details class="my-1" />
                        </td>
                        <td>
                          <v-text-field v-model="lrSubdivisionAssignments[child.id].parcel" label="Parcel Number" outlined dense
                            hide-details class="my-1" />
                        </td>
                      </tr>
                      <tr v-if="lrHasDuplicateLrNumbers">
                        <td colspan="4">
                          <v-alert type="error" dense text class="ma-1 text-caption">
                            Each LR number must be unique.
                          </v-alert>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </template>

                <!-- Amalgamation: adjudication + parcel number for the resultant PN -->
                <template v-if="lrApplicationType === 'amalgamation'">
                  <v-row class="mt-2">
                    <v-col cols="12" md="6">
                      <v-autocomplete v-model="lrAmalgamationAdjudication"
                        :items="lrAdjudications" :loading="lrLoadingAdjudications"
                        item-text="name" item-value="id" return-object
                        label="Adjudication" outlined dense />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="lrAmalgamationParcel" label="Parcel Number for Resultant PN" outlined dense />
                    </v-col>
                  </v-row>
                </template>

                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-btn color="primary" :loading="lrSubmitting" :disabled="!lrCanSubmit" @click="submitLrFormalisation">
                      Submit LR Formalisation
                    </v-btn>
                  </v-col>
                </v-row>
              </template>
            </template>
          </template>

          <!-- Deferred: Amendment Form -->
          <template v-if="isDeferred">
            <v-divider class="my-4" />
            <v-alert type="warning" dense outlined class="mb-4">
              This application has been deferred. Please review the feedback above and submit your amendments below.
            </v-alert>

            <div class="text-subtitle-2 font-weight-bold mb-2">
              Amend Application
            </div>

            <v-row v-if="canCreateApplication">
              <v-col cols="12" md="6">
                <v-select v-model="amendCategory" :items="categories" item-text="name" item-value="id" label="Category"
                  outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="amendType" :items="types" item-text="name" item-value="id" label="Type" outlined
                  dense />
              </v-col>
            </v-row>

            <!-- Re-upload documents flagged for errors -->
            <template v-if="application.documents && application.documents.length > 0">
              <div class="text-subtitle-2 font-weight-bold mb-2 mt-2">
                Re-upload Documents (only for flagged documents)
              </div>
              <template v-for="doc in application.documents">
                <v-row v-if="getDocumentFeedback(doc) && getDocumentFeedback(doc).reattach_document"
                  :key="'reupload-' + doc.id" class="align-center">
                  <v-col cols="12" md="4">
                    <span class="font-weight-medium">{{ doc.supporting_document?.name || doc.original_name }}</span>
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-file-input :label="'Re-upload ' + (doc.supporting_document?.name || doc.original_name)" outlined
                      dense accept="application/pdf"
                      @change="onFileSelected(doc.scheme_plan_supporting_document_id, $event)" />
                  </v-col>
                </v-row>
              </template>
            </template>

            <v-row>
              <v-col cols="12">
                <v-textarea v-model="amendRemarks" label="Remarks" outlined dense auto-grow rows="2" />
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <v-col cols="12">
                <v-btn color="primary" :loading="submittingAmendment" @click="submitAmendment">
                  Submit Amendment
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <!-- Approved Document Viewer Dialog -->
    <v-dialog v-model="docViewerDialog" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dense color="primary" dark>
          <v-toolbar-title>{{ viewingDoc.name }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="docLoading" title="Download" @click="downloadViewedDoc">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="docViewerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-2">
          <div v-if="docLoading" class="d-flex justify-center align-center" style="height: 80vh">
            <v-progress-circular indeterminate color="primary" size="64" />
          </div>
          <div v-else align="center">
            <pdf v-if="viewingDoc.isPdf" :key="viewerKey" :src="viewingDoc.url" />
            <v-img v-else :key="viewerKey" :src="viewingDoc.url" contain max-height="90vh" />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showAmendmentSnackbar" top :color="amendmentColor" :timeout="3000">
      {{ amendmentMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="showAmendmentSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="showLrSnackbar" top :color="lrMessageColor" :timeout="4000">
      {{ lrMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="showLrSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>
